import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type {
  EntregaDoPainel,
  EventoDeEntrega,
  MotivoDeBloqueioDoReenvio,
} from "@/api/contrato";
import {
  useListaDeEntregas,
  useReenviarEntrega,
  useSistemas,
  type FiltrosDeEntregas,
} from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { BlocoDeCodigo } from "@/componentes/BlocoDeCodigo";
import { Botao } from "@/componentes/Botao";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { ChipsDeFiltro } from "@/componentes/ChipsDeFiltro";
import { Confirmacao, type PedidoDeConfirmacao } from "@/componentes/Confirmacao";
import { Dica } from "@/componentes/Dica";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { PainelLateral } from "@/componentes/PainelLateral";
import { PainelRecolhivel } from "@/componentes/PainelRecolhivel";
import { Picote } from "@/componentes/Picote";
import { Pilula, PilulaDeSituacao, type Matiz } from "@/componentes/PilulaDeSituacao";
import { Tabela, type Coluna } from "@/componentes/Tabela";
import { useFiltrosNaUrl, type DefinicaoDeFiltro } from "@/componentes/useFiltrosNaUrl";
import { data, dataHora, dia, faz, idCurto } from "@/formato";
import { rotaDoBoleto } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { CarregarMais, contagemDaLista } from "@/telas/boletos/Boletos";
import {
  CampoDeDia,
  Escolha,
  instanteDoDia,
  nomeNaLista,
  type Opcoes,
} from "@/telas/boletos/FiltrosDeBoletos";

/*
 * Entregas: "mandou ou não mandou?", a pergunta que exigia `psql`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ REENVIAR REABRE A MESMA ENTREGA.                                         │
 * │                                                                          │
 * │ Mesmo `evento_id`, mesmo payload, uma tentativa por clique, e o cliente  │
 * │ deduplica pelo evento: nada aqui chega a `boleto.solicitado`. Quem       │
 * │ decide é o servidor, com `reenviavel`, que sai do predicado da escrita,  │
 * │ e a tela não copia a regra.                                              │
 * │                                                                          │
 * │ `boleto.falhou` é crítico porque o cliente que o lê emite de novo. Com o │
 * │ boleto fora de FALHA ele nem vem como reenviável: o aviso atrasado seria │
 * │ o segundo título.                                                        │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * A linha abre um painel local, como Indeterminados: o erro inteiro e o `evento_id` não cabem
 * em 40px, e a lista precisa ficar no mesmo lugar para a linha de baixo.
 */
type ChaveDoFiltro = keyof FiltrosDeEntregas;

const EVENTO_CRITICO = "boleto.falhou";

const ESTADOS: Record<string, { rotulo: string; matiz: Matiz }> = {
  esgotada: { rotulo: "Esgotada", matiz: "falha" },
  pendente: { rotulo: "Pendente", matiz: "transito" },
  entregue: { rotulo: "Entregue", matiz: "confirmado" },
};

const OPCOES_DE_ESTADO: Opcoes = Object.entries(ESTADOS).map(
  ([valor, { rotulo }]) => [valor, rotulo] as const,
);

/** `satisfies` reprova aqui o evento novo do contrato, em vez de deixá-lo fora do filtro. */
const EVENTOS = {
  "boleto.emitido": true,
  "boleto.registrado": true,
  "boleto.falhou": true,
  "boleto.rejeitado": true,
  "boleto.liquidado": true,
  "boleto.baixado": true,
  "boleto.descartado": true,
  "boleto.protestado": true,
  "boleto.cancelamento_falhou": true,
  "boleto.indeterminado": true,
  "documento.disponivel": true,
} satisfies Record<EventoDeEntrega, true>;

const OPCOES_DE_EVENTO: Opcoes = Object.keys(EVENTOS).map((tipo) => [tipo, tipo] as const);

/** O motivo é o código do `409` que o reenvio daria, e a `Dica` traduz. */
const BLOQUEIOS: Record<NonNullable<MotivoDeBloqueioDoReenvio>, { rotulo: string; dica: string }> = {
  entrega_fora_do_estado: {
    rotulo: "Sem reenvio",
    dica: "Só entrega esgotada reenvia.",
  },
  entrega_sem_destino: {
    rotulo: "Sem destino",
    dica: "A aplicação está sem URL ou sem segredo. Configure a entrega para reenviar.",
  },
  evento_desatualizado: {
    rotulo: "Desatualizado",
    dica: "O boleto saiu de FALHA. O aviso atrasado levaria o cliente a emitir um segundo título.",
  },
};

function rotuloDoEstado(estado: string): string {
  return ESTADOS[estado]?.rotulo ?? estado;
}

function definicoes(
  nomeDaAplicacao: (id: string) => string,
): readonly DefinicaoDeFiltro<ChaveDoFiltro>[] {
  return [
    { chave: "estado", rotulo: "Estado", textoDoValor: rotuloDoEstado },
    { chave: "tipo", rotulo: "Evento" },
    { chave: "sistema_id", rotulo: "Aplicação", textoDoValor: nomeDaAplicacao },
    { chave: "boleto_id", rotulo: "Boleto", textoDoValor: idCurto },
    { chave: "criado_de", rotulo: "Criada de", textoDoValor: dia },
    { chave: "criado_ate", rotulo: "Criada até", textoDoValor: dia },
  ];
}

function pedidoDeReenvio(entrega: EntregaDoPainel): PedidoDeConfirmacao {
  if (entrega.tipo === EVENTO_CRITICO) {
    return {
      severidade: "critica",
      titulo: `Reenviar ${EVENTO_CRITICO}?`,
      corpo:
        "O cliente que lê este evento pode emitir um segundo título. Reenvie só com o boleto conferido em FALHA.",
      verbo: "Reenviar",
      digitarParaLiberar: entrega.boleto_id.slice(-6),
      exigeJustificativa: true,
      identidade: entrega.id,
    };
  }

  return {
    severidade: "leve",
    titulo: `Reenviar ${entrega.tipo}?`,
    corpo: `A mesma entrega reabre para ${entrega.sistema_nome}, com o mesmo evento e o mesmo payload.`,
    verbo: "Reenviar",
    identidade: entrega.id,
  };
}

export function Entregas() {
  const { cliente, estado, pode } = useSessao();
  const avisos = useAvisos();
  const dentro = estado.fase === "dentro";

  const { data: sistemas } = useSistemas(cliente, dentro);
  const aplicacoes: Opcoes = (sistemas?.sistemas ?? []).map(
    (sistema) => [sistema.id, sistema.nome] as const,
  );

  const filtros = useFiltrosNaUrl(definicoes((id) => nomeNaLista(aplicacoes, id)));
  const lista = useListaDeEntregas(
    cliente,
    {
      ...filtros.valores,
      criado_de: instanteDoDia(filtros.valores.criado_de, "00:00:00"),
      criado_ate: instanteDoDia(filtros.valores.criado_ate, "23:59:59.999"),
    },
    dentro,
  );

  const reenviar = useReenviarEntrega(cliente);
  const podeOperar = pode("operador");

  const [aberta, setAberta] = useState<EntregaDoPainel | null>(null);
  const [alvo, setAlvo] = useState<EntregaDoPainel | null>(null);

  // O painel segue a lista viva: o polling de 30 s pode mudar o estado com ele aberto.
  const entregaAberta = lista.itens.find((item) => item.id === aberta?.id) ?? aberta;

  function acaoDa(entrega: EntregaDoPainel) {
    return <AcaoDaEntrega entrega={entrega} podeOperar={podeOperar} aoReenviar={setAlvo} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <PainelRecolhivel titulo="Filtros" abertoAoIniciar={false}>
        <div className="flex flex-wrap items-start gap-2">
          <Escolha
            filtros={filtros}
            chave="estado"
            rotulo="Estado"
            todas="Todos"
            opcoes={OPCOES_DE_ESTADO}
          />
          <Escolha
            filtros={filtros}
            chave="tipo"
            rotulo="Evento"
            todas="Todos"
            opcoes={OPCOES_DE_EVENTO}
          />
          <Escolha
            filtros={filtros}
            chave="sistema_id"
            rotulo="Aplicação"
            todas="Todas"
            opcoes={aplicacoes}
          />
          <CampoDeDia filtros={filtros} chave="criado_de" rotulo="Criada de" />
          <CampoDeDia filtros={filtros} chave="criado_ate" rotulo="Criada até" />
        </div>
      </PainelRecolhivel>

      <ChipsDeFiltro filtros={filtros} />

      {lista.itens.length > 0 && lista.erro === null && (
        <p className="flex items-center gap-1">
          <span className="rotulo">{contagemDaLista(lista, "entrega", "entregas")}</span>
          <Dica texto="Reenviar reabre a mesma entrega: mesmo evento e mesmo payload. A entrega concluída sai do histórico 7 dias depois do último envio." />
        </p>
      )}

      {lista.erro !== null ? (
        <FaixaDeErro erro={lista.erro} />
      ) : (
        <>
          <Tabela
            rotulo="Entregas"
            colunas={colunas(acaoDa)}
            itens={lista.itens}
            carregando={lista.carregando}
            chaveDe={(entrega) => entrega.id}
            selecionado={aberta?.id}
            aoAbrir={setAberta}
            vazio={
              filtros.aplicados.length > 0 ? "Nenhuma entrega com esses filtros." : "Nenhuma entrega."
            }
          />
          <CarregarMais lista={lista} />
        </>
      )}

      {/* Fora do ramo do erro: a releitura que falha não fecha o painel nem a confirmação. */}
      {entregaAberta !== null && (
        <DetalheDaEntrega
          entrega={entregaAberta}
          podeOperar={podeOperar}
          aoReenviar={setAlvo}
          aoFechar={() => setAberta(null)}
        />
      )}

      <Confirmacao
        pedido={alvo === null ? null : pedidoDeReenvio(alvo)}
        aoCancelar={() => setAlvo(null)}
        aoConfirmar={async (justificativa) => {
          if (alvo === null) return;

          await reenviar.mutateAsync({
            id: alvo.id,
            ...(alvo.tipo === EVENTO_CRITICO ? { justificativa } : {}),
          });
          setAlvo(null);
          // Com filtro por estado, a linha sai da lista, e o painel ficaria com o retrato velho.
          setAberta(null);
          avisos.feito("Reenvio agendado");
        }}
      />
    </div>
  );
}

/*
 * Sempre à vista, inclusive `entregue`: aqui o estado não é um normal que não se anuncia, é
 * a resposta da linha para "mandou ou não mandou?".
 */
function PilulaDoEstado({ estado }: { estado: string }) {
  return <Pilula matiz={ESTADOS[estado]?.matiz ?? "neutro"}>{rotuloDoEstado(estado)}</Pilula>;
}

function LinkDoBoleto({ entrega }: { entrega: EntregaDoPainel }) {
  if (!entrega.boleto_id) return <span className="text-texto-3">—</span>;

  return (
    <Link to={rotaDoBoleto(entrega.boleto_id)} title={entrega.boleto_id} className="rounded-full">
      <span className={entrega.boleto_situacao ? "sr-only" : "dados text-esferografica"}>
        Boleto {idCurto(entrega.boleto_id)}{" "}
      </span>
      {entrega.boleto_situacao ? <PilulaDeSituacao situacao={entrega.boleto_situacao} /> : null}
    </Link>
  );
}

function MotivoDoBloqueio({ entrega }: { entrega: EntregaDoPainel }) {
  if (entrega.motivo_bloqueio === null) return null;

  const { rotulo, dica } = BLOQUEIOS[entrega.motivo_bloqueio];
  return (
    <span className="inline-flex items-center gap-1 text-[12px] text-texto-2">
      {rotulo}
      <Dica texto={dica} />
    </span>
  );
}

function AcaoDaEntrega({
  entrega,
  podeOperar,
  aoReenviar,
}: {
  entrega: EntregaDoPainel;
  podeOperar: boolean;
  aoReenviar: (entrega: EntregaDoPainel) => void;
}) {
  if (!entrega.reenviavel) return <MotivoDoBloqueio entrega={entrega} />;
  if (!podeOperar) return null;

  return (
    <Botao
      tom="secundario"
      aria-label={`Reenviar ${entrega.tipo} de ${idCurto(entrega.id)}`}
      onClick={() => aoReenviar(entrega)}
    >
      Reenviar
    </Botao>
  );
}

/*
 * Em 1024 ficam estado, evento, boleto, validade e reenvio: o que travou, e o que dá para
 * fazer. O resto está no painel.
 */
function colunas(
  acaoDa: (entrega: EntregaDoPainel) => ReactNode,
): readonly Coluna<EntregaDoPainel>[] {
  return [
    {
      chave: "estado",
      rotulo: "Estado",
      largura: "104px",
      render: (entrega) => <PilulaDoEstado estado={entrega.estado} />,
    },
    {
      chave: "evento",
      rotulo: "Evento",
      largura: "minmax(170px, 1.2fr)",
      render: (entrega) => (
        <span className="dados truncate" title={entrega.tipo}>
          {entrega.tipo}
        </span>
      ),
    },
    {
      chave: "aplicacao",
      rotulo: "Aplicação",
      largura: "minmax(110px, 1fr)",
      descarte: 2,
      render: (entrega) => <span className="truncate text-texto-2">{entrega.sistema_nome}</span>,
    },
    {
      chave: "boleto",
      rotulo: "Boleto",
      largura: "132px",
      interativa: true,
      render: (entrega) => <LinkDoBoleto entrega={entrega} />,
    },
    {
      chave: "tentativas",
      rotulo: "Tentativas",
      largura: "84px",
      descarte: 3,
      direita: true,
      render: (entrega) => <span className="tabular">{entrega.tentativas}</span>,
    },
    {
      chave: "erro",
      rotulo: "Último erro",
      largura: "minmax(140px, 1.4fr)",
      descarte: 4,
      render: (entrega) => (
        <span className="truncate text-texto-2" title={entrega.ultimo_erro}>
          {entrega.ultimo_erro || "—"}
        </span>
      ),
    },
    {
      chave: "criada",
      rotulo: "Criada",
      largura: "84px",
      descarte: 1,
      direita: true,
      render: (entrega) => (
        <span className="tabular text-texto-2" title={dataHora(entrega.criado_em)}>
          {faz(entrega.criado_em)}
        </span>
      ),
    },
    {
      chave: "expira",
      rotulo: "Expira",
      largura: "96px",
      direita: true,
      render: (entrega) => (
        <span className="tabular text-texto-2" title={dataHora(entrega.expira_em)}>
          {data(entrega.expira_em)}
        </span>
      ),
    },
    {
      chave: "reenvio",
      rotulo: "Reenvio",
      largura: "116px",
      interativa: true,
      /*
       * Entregue e pendente não anunciam nada: o estado já é o motivo, e uma `Dica` por linha
       * seriam 200 paradas de Tab. O motivo dessas está no painel.
       */
      render: (entrega) => (entrega.estado === "esgotada" ? acaoDa(entrega) : null),
    },
  ];
}

function DetalheDaEntrega({
  entrega,
  podeOperar,
  aoReenviar,
  aoFechar,
}: {
  entrega: EntregaDoPainel;
  podeOperar: boolean;
  aoReenviar: (entrega: EntregaDoPainel) => void;
  aoFechar: () => void;
}) {
  return (
    <PainelLateral
      aberto
      aoFechar={aoFechar}
      titulo={
        <div className="flex flex-wrap items-center gap-2">
          <span className="dados text-titulo font-semibold">{entrega.tipo}</span>
          <PilulaDoEstado estado={entrega.estado} />
        </div>
      }
      descricao={`Entrega de ${entrega.tipo} para ${entrega.sistema_nome}.`}
      rodape={
        <div className="flex justify-end">
          <AcaoDaEntrega entrega={entrega} podeOperar={podeOperar} aoReenviar={aoReenviar} />
        </div>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="grid grid-cols-2 gap-2">
          <CaixaDeCampo rotulo="Aplicação">{entrega.sistema_nome}</CaixaDeCampo>
          <CaixaDeCampo rotulo="Boleto">
            <LinkDoBoleto entrega={entrega} />
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Tentativas" dados>
            {entrega.tentativas}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Criada" dados>
            {dataHora(entrega.criado_em)}
          </CaixaDeCampo>
          {entrega.entregue_em ? (
            <CaixaDeCampo rotulo="Entregue" dados>
              {dataHora(entrega.entregue_em)}
            </CaixaDeCampo>
          ) : null}
          {entrega.proxima_tentativa ? (
            <CaixaDeCampo rotulo="Próxima tentativa" dados>
              {dataHora(entrega.proxima_tentativa)}
            </CaixaDeCampo>
          ) : null}
          {entrega.expira_em ? (
            <CaixaDeCampo rotulo="Expira" dados>
              {dataHora(entrega.expira_em)}
            </CaixaDeCampo>
          ) : null}
        </div>

        <Picote />

        <CaixaDeCampo rotulo="Evento" dados>
          {entrega.evento_id}
        </CaixaDeCampo>
        {entrega.ultimo_erro ? (
          <BlocoDeCodigo rotulo="Último erro" conteudo={entrega.ultimo_erro} />
        ) : null}
      </div>
    </PainelLateral>
  );
}
